// Pure helpers for the club advent calendar («Julekalender»). No store or DOM access.
// Spec: docs/superpowers/specs/2026-11-03-club-advent-calendar-design.md

export const DOOR_TYPE_LABELS = {
    quiz: 'Quiz',
    text: 'Hilsen',
    stats: 'Dine tall',
    sponsor: 'Sponsor',
    game: 'Spill',
    challenge: 'Utfordring',
    convention: 'Stevne',
    auto: 'Automatisk',
};

// Opt-in per club: featureFlags.adventCalendar === 1 on the domain customer.
export function adventEnabled(customer) {
    return parseInt(customer?.featureFlags?.adventCalendar, 10) === 1;
}

export const DOOR_TYPES = ['auto', 'quiz', 'text', 'stats', 'sponsor', 'game', 'challenge', 'convention'];

export const ORNAMENTS = ['🎀', '⭐', '🔔', '🎄', '❄️', '🕯️', '🎅', '✨'];

// Storybook illustration for a door, served from src/assets (see bin/advent-calendar-illustrations.js).
export function doorImage(day) {
    const n = Math.min(24, Math.max(1, parseInt(day, 10) || 1));
    return `/assets/images/advent/door-${String(n).padStart(2, '0')}.png`;
}

export function ornamentFor(day) {
    const idx = Math.abs(parseInt(day, 10) || 0) % ORNAMENTS.length;
    return ORNAMENTS[idx];
}

// 'locked' | 'today' | 'open' | 'opened' from the API door view + today's door number.
export function doorState(door = {}, today = 0) {
    if (door.locked) {
        return 'locked';
    }
    if (door.opened) {
        return 'opened';
    }
    return door.day === today ? 'today' : 'open';
}

function shorten(text = '', max = 40) {
    const str = String(text || '').trim();
    return str.length > max ? `${str.slice(0, max)}…` : str;
}

// The admin picker's label for a convention («Stevne»): title and date, or the bare id.
export function conventionLabel(convention = {}, formatDate = v => v) {
    const date = convention.startDate ? formatDate(convention.startDate) : '';
    return [convention.title || '', date].filter(Boolean).join(' · ') || (convention.id ? `#${convention.id}` : '');
}

// One line per door in the admin list. `conventions` (admin list) names a convention door.
export function doorSummary(door = {}, conventions = []) {
    const label = DOOR_TYPE_LABELS[door.type] || DOOR_TYPE_LABELS.auto;
    if (!door.type || door.type === 'auto') {
        return label;
    }
    let detail = '';
    switch (door.type) {
        case 'quiz':
            detail = door.quiz?.question;
            break;
        case 'sponsor':
            detail = door.sponsor?.name || door.title;
            break;
        case 'game':
            detail = door.game;
            break;
        case 'challenge':
            detail = door.challenge;
            break;
        case 'convention': {
            const convention = (conventions || []).find(c => c && c.id === door.convention);
            detail = convention ? convention.title : (door.title || (door.convention ? `#${door.convention}` : ''));
            break;
        }
        default:
            detail = door.title;
    }
    return detail ? `${label} · ${shorten(detail)}` : label;
}

export function filledCount(doors = []) {
    return (doors || []).filter(d => d && d.type && d.type !== 'auto').length;
}

export function unopenedCount(doors = []) {
    return (doors || []).filter(d => d && !d.locked && !d.opened).length;
}

// A quiz is worth sending to the backend only when it would pass sanitizeDoorEdit there:
// a question, at least two answers with text, and a correct letter among them.
export function quizIsSaveable(quiz) {
    if (!quiz || typeof quiz !== 'object') {
        return false;
    }
    const options = (quiz.options || []).filter(o => o && String(o.text || '').trim());
    if (!String(quiz.question || '').trim() || options.length < 2) {
        return false;
    }
    return options.some(o => o.letter === quiz.correctAnswer);
}

// --- Waiting page («Julekalenderen åpner 1. desember») ---

export function countdownText(daysLeft) {
    const n = parseInt(daysLeft, 10) || 0;
    if (n <= 0) {
        return 'Åpner i dag!';
    }
    if (n === 1) {
        return 'Åpner i morgen!';
    }
    return `Åpner om ${n} dager`;
}

export function shareUrl(hostname) {
    return `https://${hostname}/index#/julekalender`;
}

export function facebookShareUrl(url) {
    return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
}

// Ready-made Norwegian post for Facebook / a newsletter. `open`: the calendar is running
// (December), so the text invites to today's door instead of to sign up.
export function sharePostText({
    clubName = 'klubben', prizes = [], url = '', open = false,
} = {}) {
    const lines = open ? [
        `🎄 Julekalenderen til ${clubName} er i gang!`,
        'Hver dag fram til jul åpner en ny luke med quiz, spill, premier og sponsortilbud.',
    ] : [
        `🎄 ${clubName} har julekalender i år!`,
        'Fra 1. desember åpner vi en ny luke hver dag med quiz, spill, premier og sponsortilbud.',
    ];
    const count = (prizes || []).length;
    if (count > 0) {
        lines.push(`Vi trekker ${count} ${count === 1 ? 'premie' : 'premier'} 24. desember.`);
    }
    lines.push(open ? `Åpne dagens luke her: ${url}` : `Meld deg på her: ${url}`);
    return lines.join('\n');
}

// The dimmed, locked gift tiles shown on the waiting page.
export function teaserDoors() {
    return [1, 2, 3];
}

// A thin stand-in for arcadeScoreStore handed to a game inside the calendar drawer: every read
// is forwarded to the real store (so MobX tracking keeps working), and a submitted run is also
// reported to `onSubmit` for the calendar's day score. No game file needs to change.
export function buildArcadeProxy({ real, onSubmit }) {
    return {
        get leaderboard() { return real?.leaderboard; },
        get me() { return real?.me; },
        get bestByGame() { return real?.bestByGame; },
        get isLoading() { return real?.isLoading; },
        get leaderboardGame() { return real?.leaderboardGame; },
        get lastSubmit() { return real?.lastSubmit; },
        loadLeaderboard: (...args) => (real?.loadLeaderboard ? real.loadLeaderboard(...args) : null),
        loadBests: (...args) => (real?.loadBests ? real.loadBests(...args) : null),
        submitScore: async (payload = {}) => {
            const result = real?.submitScore ? await real.submitScore(payload) : null;
            if (typeof onSubmit === 'function') {
                await onSubmit(payload);
            }
            return result;
        },
    };
}
